import { useEffect, useState } from "react";
import { Search, Edit2, FileText, X, ChevronLeft, ChevronRight, HeartPulse } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import LearningStyleBadge, { STYLE_META } from "../../components/LearningStyleBadge";
import { toast } from "sonner";

const GRADE_SECTIONS = [
    "1ro Primaria A", "1ro Primaria B",
    "2do Primaria A", "2do Primaria B",
    "3ro Primaria A", "3ro Primaria B",
    "4to Primaria A", "4to Primaria B",
    "5to Primaria A", "5to Primaria B",
    "6to Primaria A", "6to Primaria B",
    "1ro Secundaria A", "1ro Secundaria B",
    "2do Secundaria A", "2do Secundaria B",
    "3ro Secundaria A", "3ro Secundaria B",
    "4to Secundaria A", "4to Secundaria B",
    "5to Secundaria A", "5to Secundaria B", "5to Secundaria C",
];

const PAGE_SIZE = 10;

export default function Perfiles() {
    const [students, setStudents] = useState([]);
    const [search, setSearch] = useState("");
    const [grade, setGrade] = useState("");
    const [editing, setEditing] = useState(null);
    const [modalTop, setModalTop] = useState(200);
    const [history, setHistory] = useState(null);
    const [page, setPage] = useState(1);

    async function load() {
        const r = await api.get("/student-profile");
        setStudents(r.data || []);
        setPage(1);
    }
    useEffect(() => { load(); }, []);

    let filtered = students;
    if (search) filtered = filtered.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
    if (grade) filtered = filtered.filter((s) => s.grade === grade);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="perfiles-page">
            <h1 className="text-3xl font-semibold mb-1">Perfiles de Estudiantes</h1>
            <p className="text-edu-body mb-6">Gestiona los estilos de aprendizaje y reportes.</p>

            <div className="flex flex-wrap gap-3 mb-6">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-edu-muted" />
                    <input
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Buscar por nombre…"
                        className="edu-input w-full pl-10"
                        data-testid="search-students"
                    />
                </div>
                <select
                    value={grade}
                    onChange={(e) => { setGrade(e.target.value); setPage(1); }}
                    className="edu-input"
                    data-testid="grade-filter"
                >
                    <option value="">Todos los grados</option>
                    {GRADE_SECTIONS.map((g) => (
                        <option key={g}>{g}</option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {paginated.length === 0 && <p className="text-edu-muted col-span-2">Sin estudiantes.</p>}
                {paginated.map((s) => (
                    <div key={s.id} className="edu-card p-5">
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <h3 className="text-lg font-semibold">{s.name}</h3>
                                <p className="text-xs text-edu-muted">{s.grade || "Sin grado"}</p>
                            </div>
                            <LearningStyleBadge style={s.learning_style} />
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={(e) => {
                                    setModalTop(e.clientY - 50);
                                    setEditing(s);
                                }}
                                className="flex-1 px-3 py-2 text-xs rounded-lg bg-edu-navy text-white inline-flex items-center justify-center gap-1"
                                data-testid={`edit-profile-${s.id}`}
                            >
                                <Edit2 size={12} /> Editar Perfil
                            </button>
                            <button
                                onClick={() => loadHistory(s.id, setHistory)}
                                className="flex-1 px-3 py-2 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center justify-center gap-1"
                                data-testid={`history-${s.id}`}
                            >
                                <FileText size={12} /> Ver Historial
                            </button>
                            <Link
                                to={`/psicologo/alumnos/medico/${s.id}`}
                                className="flex-1 px-3 py-2 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center justify-center gap-1"
                            >
                                <HeartPulse size={12} /> Médico
                            </Link>
                        </div>
                    </div>
                ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-5">
                    <span className="text-xs text-edu-muted">
                        {filtered.length} estudiantes · Página {page} de {totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {editing && (
                <EditModal
                    student={editing}
                    modalTop={modalTop}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
            {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
        </div>
    );
}

async function loadHistory(id, setHistory) {
    const r = await api.get(`/student-profile/${id}`);
    setHistory(r.data);
}

function EditModal({ student, onClose, onSaved, modalTop }) {
    const [style, setStyle] = useState(student.learning_style || "visual");
    const [reason, setReason] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            let report_file_id = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/files/upload?private=true", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                report_file_id = data.id;
            }
            await api.patch(`/student-profile/${student.id}/learning-style`, {
                learning_style: style,
                reason,
                report_file_id,
            });
            toast.success("Perfil actualizado");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose}>
            <div
                className="edu-card p-6 max-w-md w-full absolute left-1/2 -translate-x-1/2"
                style={{ top: Math.min(modalTop, window.innerHeight - 480) }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{student.name}</h3>
                    <button onClick={onClose} className="text-edu-muted"><X size={18} /></button>
                </div>
                <label className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                    Estilo de aprendizaje
                </label>
                <select
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                    className="edu-input w-full mb-3"
                    data-testid="style-select"
                >
                    {Object.entries(STYLE_META).map(([v, m]) => (
                        <option key={v} value={v}>{m.label}</option>
                    ))}
                </select>
                <label className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                    Razón del cambio
                </label>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="edu-input w-full mb-3"
                    data-testid="reason-input"
                />
                <label className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                    Reporte psicológico (PDF, privado)
                </label>
                <input
                    type="file"
                    onChange={(e) => setFile(e.target.files?.[0])}
                    className="edu-input w-full mb-4"
                    data-testid="report-file"
                />
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm" data-testid="save-profile">
                        {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function HistoryModal({ data, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto edu-scroll">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Historial de {data.name}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {(data.learning_style_history || []).length === 0 ? (
                    <p className="text-sm text-edu-muted">Sin cambios registrados.</p>
                ) : (
                    <div className="space-y-3">
                        {data.learning_style_history.map((h) => (
                            <div key={h.id} className="border-b border-edu/50 pb-3">
                                <div className="text-sm">
                                    <span className="font-medium">{h.previous_style || "—"}</span> →{" "}
                                    <span className="font-medium text-edu-navy">{h.new_style}</span>
                                </div>
                                <p className="text-xs text-edu-body mt-1">{h.reason}</p>
                                <p className="text-xs text-edu-muted">
                                    {(h.created_at || "").slice(0, 10)} · {h.psychologist_name}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
